/**
 * Persist ChartDataResult to valueSummary.calculatedMetrics
 * Ensures all runtime-calculated chart data is saved to the database blob
 */

import {ChartDataResult} from '@/utils/chartDataExtractor';
import {TIME_CONFIG} from '@/../../shared/constants/timeConfig';
import { apiRequest } from '@/lib/queryClient';
import { normalizePillarName } from '@shared/pillar-constants';

export interface CalculatedMetrics {
  annualSavings: number;
  fiveYearValue: number;
  totalFTEs: number;
  deliverableAutomation: number;
  benchmarkAutomation: number;
  deliverableSavings: number;
  benchmarkSavings: number;
  totalUpsidePotential: number;
  totalSubscriptionCost: number;
  
  // Discount Logic (NEW)
  subscription_discount_percent: number;
  subscription_discount_amount: number;
  services_discount_percent: number;
  services_discount_amount: number;
  services_cost: number;
  total_investment: number;
  
  pillarBreakdown: {
    [pillarName: string]: {
      ftes: number;
      hoursSaved: number;
      savings: number;
    };
  };
  calculatedAt: string;
  dataVersion: string;
}

/**
 * Convert ChartDataResult to CalculatedMetrics format
 */
export function convertChartDataToCalculatedMetrics(
  chartData: ChartDataResult,
  benchmarkData: any[] = []
): CalculatedMetrics {

  // Prefer values already computed in chartData (authoritative UI pipeline)
  const benchmarkAutomation =
    (typeof chartData.benchmarkWeightedAutomation === 'number' && chartData.benchmarkWeightedAutomation > 0)
      ? chartData.benchmarkWeightedAutomation
      : calculateBenchmarkAutomation(chartData.taskSavingsData, benchmarkData);

  const benchmarkSavings =
    (typeof (chartData as any).totalBenchmarkSavings === 'number' && (chartData as any).totalBenchmarkSavings > 0)
      ? (chartData as any).totalBenchmarkSavings
      : calculateBenchmarkSavings(chartData.taskSavingsData, benchmarkData);
  

  // Calculate deliverable automation from current task data
  const deliverableAutomation = chartData.weightedAutomationPotential || chartData.deliverableAutomationPotential || 0;
  
  // Calculate total upside potential (benchmark - deliverable)
  const totalUpsidePotential = Math.max(0, benchmarkSavings - chartData.totalAnnualSavings);
  
  // Create pillar breakdown from granular activity breakdown
  const pillarBreakdown = createPillarBreakdown(chartData.granularActivityBreakdown || []);

  return {
      annualSavings: chartData.totalAnnualSavings || 0,
      fiveYearValue: (chartData.totalAnnualSavings * 5),
      totalFTEs: chartData.totalFTEs || 0,
      deliverableAutomation: deliverableAutomation,
      benchmarkAutomation: benchmarkAutomation,
      deliverableSavings: chartData.totalAnnualSavings || 0,
      benchmarkSavings: benchmarkSavings,
      totalUpsidePotential: totalUpsidePotential,
      totalSubscriptionCost: 0, // Will be populated from pricing calculation

      // Discount Logic - will be populated from pricing data
      subscription_discount_percent: 0,
      subscription_discount_amount: 0,
      services_discount_percent: 0,
      services_discount_amount: 0,
      services_cost: 0,
      total_investment: 0,

      pillarBreakdown: pillarBreakdown,
      calculatedAt: new Date().toISOString(),
      dataVersion: 'v2.1'
  };
}

/**
 * Calculate benchmark automation percentage
 */
function calculateBenchmarkAutomation(taskSavingsData: any[], benchmarkData: any[]): number {
  if (!taskSavingsData?.length || !benchmarkData?.length) return 0;

  // Build lookup map using canonical fields with safe fallbacks
  const benchmarkMap = new Map<string, number>();
  benchmarkData.forEach((b: any) => {
    const key = String(b.task_label || b.process_l4 || '').toLowerCase();
    const percent = Number(b.benchmark_percent ?? b.automation_level ?? 0) || 0;
    if (key) benchmarkMap.set(key, percent);
  });

  let totalWeightedBenchmark = 0;
  let totalFTEs = 0;

  taskSavingsData.forEach((task: any) => {
    const taskKey = String(task.taskName || '').toLowerCase();
    const benchmarkLevel = benchmarkMap.get(taskKey) || 0;
    const ftes = Number(task.fteCount || task.totalFTEs || 0) || 0;
    totalWeightedBenchmark += benchmarkLevel * ftes;
    totalFTEs += ftes;
  });

  return totalFTEs > 0 ? (totalWeightedBenchmark / totalFTEs) : 0;
}

/**
 * Calculate benchmark savings based on benchmark automation levels
 */
function calculateBenchmarkSavings(taskSavingsData: any[], benchmarkData: any[]): number {
  if (!taskSavingsData?.length || !benchmarkData?.length) return 0;

  const benchmarkMap = new Map<string, number>();
  benchmarkData.forEach((b: any) => {
    const key = String(b.task_label || b.process_l4 || '').toLowerCase();
    const percent = Number(b.benchmark_percent ?? b.automation_level ?? 0) || 0;
    if (key) benchmarkMap.set(key, percent);
  });

  let totalBenchmarkSavings = 0;

  taskSavingsData.forEach((task: any) => {
    const taskKey = String(task.taskName || '').toLowerCase();
    const benchmarkLevel = benchmarkMap.get(taskKey) || 0;
    const currentSavings = Number(task.annualSavings || 0) || 0;
    const currentAutomation = Number(task.automationPercent || 0) || 0;

    if (currentAutomation > 0 && benchmarkLevel > 0) {
      // Calculate what savings would be at the benchmark automation level
      const baseSavings = currentSavings / (currentAutomation / 100);
      const benchmarkSavings = baseSavings * (benchmarkLevel / 100);
      totalBenchmarkSavings += benchmarkSavings;
    }
  });

  return totalBenchmarkSavings;
}

/**
 * Create pillar breakdown from granular activity breakdown
 */
function createPillarBreakdown(granularBreakdown: any[]): Record<string, { ftes: number; hoursSaved: number; savings: number; }> {
  const pillarBreakdown: Record<string, { ftes: number; hoursSaved: number; savings: number; }> = {};

  granularBreakdown.forEach(activity => {
    const rawPillar = activity.pillar || 'Other Processes';
    const pillar = normalizePillarName(rawPillar); // Normalize pillar names for consistency
    const ftes = activity.totalFTEs || 0;
    const savings = activity.annualSavings || 0;
    const hoursSaved = ftes * TIME_CONFIG.HOURS_PER_FTE_YEAR; // Authoritative formula: 1,536 hours/FTE/year

    if (!pillarBreakdown[pillar]) {
      pillarBreakdown[pillar] = { ftes: 0, hoursSaved: 0, savings: 0 };
    }

    pillarBreakdown[pillar].ftes += ftes;
    pillarBreakdown[pillar].hoursSaved += hoursSaved;
    pillarBreakdown[pillar].savings += savings;
  });

  return pillarBreakdown;
}

/**
 * Persist calculated metrics to database via API
 */
export async function persistCalculatedMetrics(
  projectId: string, 
  calculatedMetrics: CalculatedMetrics
): Promise<boolean> {
  try {
    await apiRequest(`/api/value-assessment/${projectId}/persist-calculated-metrics`, {
      method: 'POST',
      body: { calculatedMetrics }
    });
    return true;
  } catch (error) {
    console.error('❌ PERSIST ERROR:', error);
    return false;
  }
}

/**
 * Main function to calculate and persist metrics from chart data
 */
export async function calculateAndPersistMetrics(
  projectId: string,
  chartData: ChartDataResult,
  benchmarkData: any[] = []
): Promise<CalculatedMetrics | null> {
  try {

    // Convert chart data to the calculated metrics format
    const calculatedMetrics = convertChartDataToCalculatedMetrics(chartData, benchmarkData);
    
    // Fetch current pricing data to calculate total subscription cost and discount info
    try {
      const projectData = await apiRequest(`/api/value-assessment/${projectId}`);
        const roiData = projectData.roi_data?.dynamicMetrics;
        const summaryData = projectData.summary_data;
        
        if (roiData?.totalInvestment) {
          calculatedMetrics.totalSubscriptionCost = roiData.totalInvestment;
          calculatedMetrics.total_investment = roiData.totalInvestment;
        }
        
        // Extract discount information from summary data or pricing calculation
        if (summaryData?.discountSoftwarePercent) {
          calculatedMetrics.subscription_discount_percent = parseFloat(summaryData.discountSoftwarePercent.replace('%', ''));
        }
        if (summaryData?.discountServicesPercent) {
          calculatedMetrics.services_discount_percent = parseFloat(summaryData.discountServicesPercent.replace('%', ''));
        }
        if (summaryData?.discountedServicesCost) {
          calculatedMetrics.services_cost = summaryData.discountedServicesCost;
        }
        if (summaryData?.originalAnnualSubscription && summaryData?.discountedAnnualSubscription) {
          calculatedMetrics.subscription_discount_amount = summaryData.originalAnnualSubscription - summaryData.discountedAnnualSubscription;
        }
        if (summaryData?.originalServicesCost && summaryData?.discountedServicesCost) {
          calculatedMetrics.services_discount_amount = summaryData.originalServicesCost - summaryData.discountedServicesCost;
        }
    } catch (pricingError) {
      // console.warn('⚠️ Could not fetch pricing data for total subscription cost:', pricingError);
        console.info(pricingError)
    }
    
    // Persist to the database
    const success = await persistCalculatedMetrics(projectId, calculatedMetrics);
    
    if (success) {
      return calculatedMetrics;
    } else {
      console.error('❌ PERSISTENCE FAILED');
      return null;
    }
  } catch (error) {
    console.error('❌ CALCULATION AND PERSISTENCE ERROR:', error);
    return null;
  }
}
